<template>
  <section class="min-h-[calc(100vh-4.25rem)] bg-slate-50 px-2 py-3 sm:px-4 lg:px-8">
    <div
      class="chat-shell mx-auto grid h-[calc(100vh-5.75rem)] max-w-7xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm lg:h-[calc(100vh-6.25rem)] lg:grid-cols-[21rem_minmax(0,1fr)]"
    >
      <aside
        class="min-h-0 flex-col border-slate-200 lg:flex lg:border-r"
        :class="compactChatOpen ? 'hidden' : 'flex'"
      >
        <div class="border-b border-slate-200 p-3 sm:p-4">
          <div class="flex items-center justify-between gap-3">
            <div class="flex min-w-0 items-start gap-3">
              <HistoryBackButton />
              <div class="min-w-0">
                <p class="m-0 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Messages</p>
                <h1 class="mt-1 text-xl font-semibold text-slate-950">Conversations</h1>
              </div>
            </div>
            <span class="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
              {{ visibleConversations.length }}
            </span>
          </div>

          <label class="relative mt-4 block">
            <span class="sr-only">Search chats</span>
            <svg class="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
            <input
              v-model="search"
              class="min-h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-10 text-sm font-medium text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100"
              type="search"
              placeholder="Search conversations or flyers"
            />
          </label>
        </div>

        <div class="min-h-0 flex-1 overflow-y-auto p-2">
          <div class="grid gap-1">
            <p class="px-2 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Chat Conversations</p>
            <div v-if="chatsLoading" class="px-4 py-8 text-center text-sm font-semibold text-slate-500">
              Loading conversations...
            </div>
            <div v-else-if="chatsError" class="mx-2 rounded-2xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
              {{ chatsError }}
            </div>
            <button
              v-for="conversation in filteredConversations"
              :key="conversation.id"
              class="flex w-full gap-3 rounded-xl border border-transparent p-2.5 text-left transition hover:border-slate-200 hover:bg-slate-50"
              :class="conversation.id === activeConversation.id ? 'border-blue-100 bg-blue-50/80' : ''"
              type="button"
              @click="selectConversation(conversation.id)"
            >
              <span class="relative h-11 w-11 shrink-0">
                <img
                  v-if="shouldShowConversationImage(conversation)"
                  class="h-11 w-11 rounded-full bg-slate-100 object-cover"
                  :src="getConversationContact(conversation).image"
                  :alt="formatContactName(getConversationContact(conversation).name)"
                  loading="lazy"
                  @error="markConversationImageFailed(conversation)"
                />
                <span v-else class="grid h-11 w-11 place-items-center rounded-full bg-blue-600 text-sm font-semibold text-white">
                  {{ getInitials(formatContactName(getConversationContact(conversation).name)) }}
                </span>
                <span
                  class="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white"
                  :class="getConversationRealtimeDotClass(conversation)"
                  :aria-label="isConversationRealtimeActive(conversation) ? 'Active chat' : 'Inactive chat'"
                  role="img"
                ></span>
              </span>
              <span class="min-w-0 flex-1">
                <span class="flex items-center justify-between gap-2">
                  <span class="truncate text-sm font-semibold text-slate-950">{{ formatConversationNamePreview(getConversationContact(conversation).name) }}</span>
                  <span class="shrink-0 text-[11px] font-medium text-slate-400">{{ formatConversationListTime(conversation) }}</span>
                </span>
                <span v-if="getThreadCount(conversation)" class="mt-0.5 block truncate text-xs font-medium text-slate-500">
                  {{ getThreadCount(conversation) }} flyer histor{{ getThreadCount(conversation) === 1 ? 'y' : 'ies' }}
                </span>
                <span class="mt-1 block truncate text-sm text-slate-600">{{ formatConversationPreview(conversation.lastMessage) }}</span>
              </span>
              <span v-if="conversation.unread" class="mt-1 grid h-5 min-w-5 place-items-center rounded-full bg-blue-600 px-1.5 text-[11px] font-semibold text-white">
                {{ conversation.unread }}
              </span>
            </button>

            <div v-if="!chatsLoading && !filteredConversations.length" class="px-4 py-8 text-center">
              <p class="m-0 text-sm font-bold text-slate-900">No conversations found</p>
              <p class="mt-1 text-sm text-slate-500">Try another user or message.</p>
            </div>
          </div>

        </div>
      </aside>

      <main
        class="chat-panel min-h-0 min-w-0 grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden"
        :class="hasActiveConversation ? (compactChatOpen ? 'grid' : 'hidden lg:grid') : 'hidden'"
      >
        <header class="sticky top-0 z-30 flex min-w-0 items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3 sm:px-5">
          <div class="flex min-w-0 items-center gap-3">
            <button
              class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-4 focus:ring-blue-100 lg:hidden"
              type="button"
              aria-label="Back to conversations"
              @click="closeCompactChat"
            >
              <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="m15 18-6-6 6-6" />
              </svg>
            </button>
            <img
              v-if="shouldShowConversationImage(activeConversation)"
              class="h-11 w-11 shrink-0 rounded-full bg-slate-100 object-cover"
              :src="activeContact.image"
              :alt="activeContact.name"
              loading="lazy"
              @error="markConversationImageFailed(activeConversation)"
            />
            <div v-else class="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-blue-600 text-sm font-semibold text-white">
              {{ activeInitials }}
            </div>
            <div class="min-w-0">
              <h2 class="truncate text-base font-semibold text-slate-950 sm:text-lg">{{ activeContact.name }}</h2>
              <p class="flex min-w-0 flex-wrap items-center gap-2 text-sm font-medium text-slate-500">
                <span v-if="activePeerTyping" class="inline-flex items-center gap-1.5 truncate font-semibold text-blue-600">
                  <span class="flex gap-0.5" aria-hidden="true">
                    <span class="h-1 w-1 animate-pulse rounded-full bg-blue-500"></span>
                    <span class="h-1 w-1 animate-pulse rounded-full bg-blue-500 [animation-delay:120ms]"></span>
                    <span class="h-1 w-1 animate-pulse rounded-full bg-blue-500 [animation-delay:240ms]"></span>
                  </span>
                  Typing...
                </span>
                <span v-else class="truncate">{{ activeContact.status || 'Chat conversation' }}</span>
                <span
                  class="inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-[0.68rem] font-semibold"
                  :class="chatRealtimeStatusClass"
                >
                  <span class="h-1.5 w-1.5 rounded-full" :class="chatRealtimeDotClass"></span>
                  {{ chatRealtimeStatusLabel }}
                </span>
              </p>
            </div>
          </div>
          <span v-if="isFlyerThreadMode" class="hidden rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-500 sm:inline-flex">
            {{ getThreadCount(activeConversation) }} flyer thread{{ getThreadCount(activeConversation) === 1 ? '' : 's' }}
          </span>
        </header>

        <div ref="messagePane" class="chat-pane min-h-0 min-w-0 overscroll-contain overflow-y-auto px-2 py-4 sm:px-5 sm:py-5 lg:px-6">
          <div v-if="detailLoading" class="grid h-full place-items-center text-sm font-semibold text-slate-500">
            Loading chat detail...
          </div>
          <div v-else-if="detailError" class="mx-auto w-full max-w-5xl rounded-2xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
            {{ detailError }}
          </div>
          <div v-else class="mx-auto grid w-full max-w-5xl min-w-0 gap-5">
            <section v-for="group in groupedChatTimeline" :key="group.key" class="grid min-w-0 gap-3">
              <div class="sticky top-0 z-10 flex justify-center py-1 backdrop-blur">
                <span class="rounded-full bg-white px-3 py-1 text-[11px] font-semibold text-slate-500 shadow-sm ring-1 ring-slate-200">
                  {{ group.label }}
                </span>
              </div>

              <div class="grid min-w-0 gap-2.5">
                <div
                  v-for="item in group.items"
                  :key="item.id"
                  class="flex min-w-0"
                  :class="item.showFlyerContext ? 'justify-center' : item.message.mine ? 'justify-end' : 'justify-start'"
                >
                  <div
                    class="grid min-w-0 gap-1"
                    :class="[
                      item.showFlyerContext ? 'w-full max-w-4xl' : 'max-w-[92%] sm:max-w-[80%] md:max-w-[72%] lg:max-w-[68%]',
                      item.message.mine ? 'justify-items-end' : 'justify-items-start'
                    ]"
                  >
                    <div v-if="item.showFlyerContext" class="flyer-thread-card w-full max-w-4xl min-w-0 justify-self-center rounded-2xl border border-blue-200 bg-white/95 p-3 text-left shadow-sm">
                      <div class="flex gap-2.5">
                        <img
                          v-if="item.flyer.image"
                          class="h-12 w-12 shrink-0 rounded-xl bg-blue-50 object-cover"
                          :src="item.flyer.image"
                          :alt="item.flyer.title || item.flyer.category || 'Flyer'"
                          loading="lazy"
                        />
                        <span v-else class="flyer-image-placeholder h-12 w-12 shrink-0 rounded-xl">
                          {{ getFlyerPlaceholderLetter(item.flyer) }}
                        </span>
                        <div class="min-w-0 flex-1">
                          <div class="flex min-w-0 items-center gap-2">
                            <span v-if="item.flyer.category" class="shrink-0 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-blue-700">
                              {{ item.flyer.category }}
                            </span>
                            <span v-if="item.flyer.status" class="truncate text-[11px] font-semibold text-emerald-700">
                              {{ item.flyer.status }}
                            </span>
                          </div>
                          <p class="m-0 mt-1 line-clamp-1 text-sm font-semibold text-slate-950">
                            {{ getFlyerThreadTitle(item.flyer) }}
                          </p>
                          <div
                            v-if="getFlyerDescriptionText(item.flyer.description)"
                            class="flyer-thread-description mt-0.5 line-clamp-2 text-xs leading-5 text-slate-500"
                            v-html="formatFlyerDescriptionHtml(item.flyer.description)"
                          />
                          <div class="mt-2 flex flex-wrap items-center gap-2">
                            <router-link
                              class="inline-flex min-h-7 items-center rounded-full bg-blue-50 px-2.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                              :to="`/flyers/${item.flyer.id}`"
                              @click.stop
                            >
                              Open flyer
                            </router-link>
                            <button
                              class="nf-primary-button nf-primary-button--compact"
                              type="button"
                              @click.stop="openShareFlyer(item.flyer)"
                            >
                              Share
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      class="chat-bubble max-w-full rounded-2xl px-3.5 py-2.5 shadow-sm"
                      :class="item.message.mine ? 'chat-bubble-out rounded-br-md text-white' : 'chat-bubble-in rounded-bl-md text-slate-800'"
                    >
                      <a
                        v-if="getMessageLinkPreview(item)"
                        class="chat-link-preview mb-2 grid overflow-hidden rounded-xl text-left"
                        :class="item.message.mine ? 'chat-link-preview-out' : 'chat-link-preview-in'"
                        :href="getMessageLinkPreview(item).url"
                        target="_blank"
                        rel="noopener noreferrer"
                        @click.stop
                      >
                        <span class="flex min-w-0 items-center gap-2 px-3 py-2">
                          <img
                            class="h-5 w-5 shrink-0 rounded bg-white/80 object-contain"
                            :src="getMessageLinkPreview(item).favicon"
                            alt=""
                            loading="lazy"
                            @error="$event.currentTarget.style.display = 'none'"
                          />
                          <span class="min-w-0">
                            <span class="block truncate text-[11px] font-semibold uppercase tracking-[0.08em] opacity-75">
                              {{ getMessageLinkPreview(item).domain }}
                            </span>
                            <span class="mt-0.5 block truncate text-sm font-semibold">
                              {{ getMessageLinkPreview(item).title }}
                            </span>
                          </span>
                        </span>
                        <span class="truncate border-t px-3 py-2 text-xs font-medium opacity-80" :class="item.message.mine ? 'border-white/20' : 'border-slate-200'">
                          {{ getMessageLinkPreview(item).displayUrl }}
                        </span>
                      </a>
                      <p class="m-0 whitespace-pre-wrap text-sm leading-6">
                        <template v-for="part in getVisibleMessageBodyParts(item)" :key="part.key">
                          <a
                            v-if="part.url"
                            class="font-semibold underline underline-offset-2"
                            :class="item.message.mine ? 'text-white hover:text-blue-50' : 'text-blue-700 hover:text-blue-800'"
                            :href="part.url"
                            target="_blank"
                            rel="noopener noreferrer"
                            @click.stop
                          >{{ part.text }}</a>
                          <template v-else>{{ part.text }}</template>
                        </template>
                      </p>
                      <button
                        v-if="isLongMessage(item)"
                        class="mt-1 text-xs font-semibold"
                        :class="item.message.mine ? 'text-blue-50 hover:text-white' : 'text-blue-700 hover:text-blue-800'"
                        type="button"
                        @click="toggleMessageExpanded(item.id)"
                      >
                        {{ isMessageExpanded(item.id) ? 'Show less' : 'Show more' }}
                      </button>
                      <p
                        class="mt-1 flex items-center justify-end gap-1 text-[11px] font-medium"
                        :class="item.message.mine ? 'text-blue-100' : 'text-slate-400'"
                      >
                        <span>{{ item.message.time }}</span>
                        <svg
                          v-if="item.message.mine"
                          class="h-3.5 w-4 shrink-0"
                          viewBox="0 0 18 12"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="1.7"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          aria-label="Delivered"
                          role="img"
                        >
                          <path d="M1.5 6.5 4.6 9.6 10.8 2.4" />
                          <path d="M7.2 8.6 8.2 9.6 16.5 2.4" />
                        </svg>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>

        <form class="sticky bottom-0 z-30 min-w-0 border-t border-slate-200 bg-white p-3 sm:p-4" @submit.prevent="sendMessage">
          <div class="mx-auto flex min-w-0 w-full max-w-5xl items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-1.5 sm:rounded-full">
            <textarea
              v-model="draft"
              class="min-h-10 min-w-0 flex-1 resize-none overflow-hidden border-0 bg-transparent px-3 py-2.5 text-base font-medium text-slate-900 outline-none placeholder:text-slate-400 focus:outline-none focus:ring-0 sm:px-4 lg:text-sm"
              rows="1"
              placeholder="Message"
              @input="handleTypingInput"
              @blur="stopTyping"
              @keydown.enter="handleComposerEnter"
            />
            <div class="relative shrink-0">
              <button
                class="inline-flex h-10 w-10 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-950"
                type="button"
                :aria-expanded="emojiPickerOpen"
                aria-label="Choose emoji"
                @click="emojiPickerOpen = !emojiPickerOpen"
              >
                <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                  <path d="M9 9h.01" />
                  <path d="M15 9h.01" />
                </svg>
              </button>

              <div
                v-if="emojiPickerOpen"
                class="absolute bottom-[calc(100%+0.75rem)] right-0 z-50 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-950/20"
              >
                <emoji-picker class="chat-emoji-picker" @emoji-click="insertEmoji" />
              </div>
            </div>
            <button
              v-if="draft.trim()"
              class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
              type="submit"
              aria-label="Send message"
            >
              <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M22 2 11 13" />
                <path d="m22 2-7 20-4-9-9-4 20-7Z" />
              </svg>
            </button>
          </div>
        </form>
      </main>

      <section
        v-if="!hasActiveConversation"
        class="chat-pane hidden min-h-0 min-w-0 border-l border-slate-200 lg:block"
        aria-label="No chat selected"
      />
    </div>
    <Teleport to="body">
      <ShareFlyerModal v-if="shareFlyer" :flyer="shareFlyer" @close="shareFlyer = null" />
    </Teleport>
  </section>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useHead } from '@vueuse/head'
import 'emoji-picker-element'
import HistoryBackButton from '@/components/common/HistoryBackButton.vue'
import ShareFlyerModal from '@/components/common/ShareFlyerModal.vue'
import api from '@/services/api'
import { connectChatRealtime, emitChatRealtime } from '@/services/chatRealtime'
import { SESSION_LOGOUT_EVENT, useSessionStore } from '@/stores/session'
import { buildImageUrl } from '@/utils/flyerDetail'

useHead({
  title: 'Chats | NetFlyr',
  meta: [
    { name: 'description', content: 'Open NetFlyr chats to message flyer owners, follow up on local posts, share flyer context, manage conversations, and connect with nearby users about events, jobs, rides, and services.' },
    { name: 'keywords', content: 'NetFlyr chats, flyer messages, local conversations, community chat, message flyer owners, nearby users' }
  ]
})

const route = useRoute()
const session = useSessionStore()
const search = ref('')
const draft = ref('')
const messagePane = ref(null)
const emojiPickerOpen = ref(false)
const shareFlyer = ref(null)
const chatsLoading = ref(false)
const chatsError = ref('')
const detailLoading = ref(false)
const detailError = ref('')
const failedConversationImages = ref(new Set())
const expandedMessageIds = ref(new Set())
const MESSAGE_PREVIEW_LIMIT = 220

const emptyFlyer = {
  id: '',
  title: '',
  category: 'Flyer',
  description: '',
  image: '',
  location: '',
  address: '',
  status: 'Active'
}

const emptyThread = {
  id: '',
  flyer: emptyFlyer,
  lastMessage: '',
  lastTime: '',
  lastDateValue: '',
  unread: 0,
  dateKey: '',
  dateLabel: '',
  dateSortValue: 0,
  messages: []
}

const emptyConversation = {
  id: '',
  contact: { id: '', name: 'Chat', status: '', image: '' },
  lastTime: '',
  lastDateValue: '',
  unread: 0,
  lastMessage: '',
  flyerThreads: []
}

const startOfDay = (date) => {
  const next = new Date(date)
  next.setHours(0, 0, 0, 0)
  return next
}

const addDays = (date, days) => {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

const formatChatDate = (date) =>
  new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(date)

const getDateMeta = (date) => {
  const day = startOfDay(date)
  const today = startOfDay(new Date())
  const yesterday = addDays(today, -1)

  if (day.getTime() === today.getTime()) {
    return { key: day.toISOString(), label: 'Today', sortValue: day.getTime() }
  }
  if (day.getTime() === yesterday.getTime()) {
    return { key: day.toISOString(), label: 'Yesterday', sortValue: day.getTime() }
  }
  return { key: day.toISOString(), label: formatChatDate(day), sortValue: day.getTime() }
}

const isSameDay = (left, right) => startOfDay(left).getTime() === startOfDay(right).getTime()

const isBlankLike = (value) => {
  if (value === undefined || value === null) return true
  const normalized = String(value).trim().toLowerCase()
  return !normalized || ['0', 'null', 'undefined', 'nan', 'no name', 'no_name'].includes(normalized)
}

const firstValue = (...values) => values.find((value) => !isBlankLike(value))
const currentUserId = () => String(session.userId || session.user?.user_id || session.user?.userid || session.user?.uid || '')
const CHAT_TARGET_STORAGE_KEY = 'netflyr.chatTarget'
const firstNonZeroValue = (...values) =>
  values.find((value) => {
    const normalized = String(value ?? '').trim()
    return normalized && normalized !== '0'
  })

const getStoredChatTarget = () => {
  try {
    const stored = JSON.parse(sessionStorage.getItem(CHAT_TARGET_STORAGE_KEY) || '{}') || {}
    return stored
  } catch {
    return {}
  }
}

const getRouteChatTarget = () => {
  const stored = getStoredChatTarget()
  return {
    toId: String(firstValue(route.query.to_id, route.query.toUID, route.query.user_id, stored.to_id, stored.toId, '')).trim(),
    flyerId: String(firstValue(route.query.flyer_id, route.query.flyerID, stored.flyer_id, stored.flyerId, '')).trim(),
    name: String(firstValue(route.query.to_name, stored.to_name, stored.toName, stored.name, '')).trim(),
    image: String(firstValue(route.query.to_image, stored.to_image, stored.toImage, stored.image, '')).trim(),
    flyerTitle: String(firstValue(stored.flyer_title, stored.flyerTitle, '')).trim(),
    flyerDescription: String(firstValue(stored.flyer_description, stored.flyerDescription, '')).trim(),
    flyerImage: String(firstValue(stored.flyer_image, stored.flyerImage, '')).trim(),
    flyerCategory: String(firstValue(stored.flyer_category, stored.flyerCategory, '')).trim(),
    source: String(firstValue(route.query.source, stored.source, '')).trim()
  }
}

const clearStoredChatTarget = () => {
  try {
    sessionStorage.removeItem(CHAT_TARGET_STORAGE_KEY)
  } catch {
    // Ignore storage access failures in restricted browser contexts.
  }
}

const extractRows = (payload) => {
  if (Array.isArray(payload)) return payload
  if (!payload || typeof payload !== 'object') return []

  const candidates = [
    payload.list,
    payload.data,
    payload.result,
    payload.chats,
    payload.messages,
    payload.data?.list,
    payload.data?.data,
    payload.data?.chats,
    payload.data?.messages
  ]

  return candidates.find(Array.isArray) || []
}

const extractDetailRows = (payload) => {
  if (Array.isArray(payload)) return payload
  if (!payload || typeof payload !== 'object') return []

  const arrays = []
  const seen = new Set()
  const collect = (value) => {
    if (!Array.isArray(value) || seen.has(value)) return
    seen.add(value)
    arrays.push(...value)
  }
  const collectFromObject = (source = {}) => {
    if (!source || typeof source !== 'object' || Array.isArray(source)) return
    collect(source.list)
    collect(source.data)
    collect(source.result)
    collect(source.chats)
    collect(source.chat)
    collect(source.messages)
    collect(source.normal_chats)
    collect(source.normal_chat)
    collect(source.user_chats)
    collect(source.direct_chats)
    collect(source.flyer_chats)
    collect(source.flyer_chat)
    collect(source.flyerThreads)
    collect(source.threads)
    collect(source.flyers)
    collect(source.items)
  }

  collectFromObject(payload)
  collectFromObject(payload.data)
  return arrays
}

const hasFlyerThreadData = (row = {}) => {
  if (!row || typeof row !== 'object') return false
  if (Array.isArray(firstValue(row.flyerThreads, row.threads, row.flyers, row.items, null))) return true
  return Boolean(firstNonZeroValue(
    row.thread_id,
    row.flyer_chat_id,
    row.flyer_id,
    row.last_flyer_id,
    row.flyer?.flyer_id,
    row.flyer?.id,
    row.flyer_title,
    row.flyer_image,
    row.flyer_description,
    row.share_url
  ))
}

const parseChatDate = (value) => {
  if (!value) return new Date()
  const normalized = String(value).includes(' ') ? String(value).replace(' ', 'T') : value
  const parsed = new Date(normalized)
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed
}

const decodeHtmlEntities = (value) =>
  String(value || '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')

const stripHtml = (value) =>
  decodeHtmlEntities(value)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')

const decodeText = (value) => stripHtml(value)

const cleanText = (value) => {
  const text = decodeText(value).trim()
  return isBlankLike(text) ? '' : text
}

const normalizeContactImage = (value) => {
  const image = cleanText(value)
  if (!image || ['no_image', 'no image'].includes(image.toLowerCase())) return ''
  return buildImageUrl(image)
}

const getConversationImageKey = (conversation = {}) => {
  const contact = getConversationContact(conversation)
  return `${conversation.id || contact.id || 'chat'}:${contact.image || ''}`
}

const shouldShowConversationImage = (conversation = {}) => {
  const contact = getConversationContact(conversation)
  return Boolean(contact.image && !failedConversationImages.value.has(getConversationImageKey(conversation)))
}

const markConversationImageFailed = (conversation = {}) => {
  const nextFailedImages = new Set(failedConversationImages.value)
  nextFailedImages.add(getConversationImageKey(conversation))
  failedConversationImages.value = nextFailedImages
}

const formatConversationPreview = (value, limit = 30) => {
  const text = cleanText(value)
  if (!text) return 'No messages yet'
  if (text.length <= limit) return text
  return `${text.slice(0, limit).trimEnd()}....`
}

const formatConversationNamePreview = (name, limit = 24) => {
  const text = formatContactName(name)
  if (text.length <= limit) return text
  return `${text.slice(0, limit).trimEnd()}...`
}

const sanitizeRichText = (value) => {
  const html = decodeHtmlEntities(value).trim()
  if (!html) return ''

  return html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '')
    .replace(/\s+on[a-z]+\s*=\s*(['"]).*?\1/gi, '')
    .replace(/\s+on[a-z]+\s*=\s*[^\s>]+/gi, '')
    .replace(/\s+href\s*=\s*(['"])\s*javascript:[\s\S]*?\1/gi, '')
}

const formatFlyerDescriptionHtml = (description) => {
  const richText = sanitizeRichText(description)
  if (!richText) return ''
  if (/<[a-z][\s\S]*>/i.test(richText)) return richText
  return richText.replace(/\n/g, '<br>')
}

const URL_PATTERN = /((?:https?:\/\/|www\.)[^\s<>"']+)/gi
const URL_TRAILING_PUNCTUATION_PATTERN = /[),.!?;:]+$/

const splitUrlToken = (token = '') => {
  const trailing = token.match(URL_TRAILING_PUNCTUATION_PATTERN)?.[0] || ''
  return {
    urlText: trailing ? token.slice(0, -trailing.length) : token,
    trailing
  }
}

const normalizeMessageUrl = (value = '') => {
  const raw = String(value || '').trim()
  if (!raw) return ''
  const href = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`

  try {
    const parsed = new URL(href)
    if (!['http:', 'https:'].includes(parsed.protocol)) return ''
    return parsed.href
  } catch {
    return ''
  }
}

const extractFirstMessageUrl = (text = '') => {
  URL_PATTERN.lastIndex = 0
  const match = URL_PATTERN.exec(String(text || ''))
  if (!match) return null
  const { urlText } = splitUrlToken(match[0])
  const normalizedUrl = normalizeMessageUrl(urlText)
  return normalizedUrl ? { text: urlText, url: normalizedUrl } : null
}

const formatLinkPreviewTitle = (url) => {
  const segments = url.pathname
    .split('/')
    .map((segment) => decodeURIComponent(segment).replace(/[-_]+/g, ' ').trim())
    .filter(Boolean)

  if (!segments.length) return url.hostname.replace(/^www\./i, '')
  return segments[segments.length - 1]
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const getMessageLinkPreview = (item = {}) => {
  const firstUrl = extractFirstMessageUrl(getMessageBodyText(item))
  if (!firstUrl) return null

  try {
    const parsed = new URL(firstUrl.url)
    const domain = parsed.hostname.replace(/^www\./i, '')
    return {
      url: firstUrl.url,
      domain,
      title: formatLinkPreviewTitle(parsed),
      displayUrl: firstUrl.text,
      favicon: `${parsed.origin}/favicon.ico`
    }
  } catch {
    return null
  }
}

const splitMessageBodyParts = (text = '') => {
  const value = String(text || '')
  const parts = []
  let lastIndex = 0
  let partIndex = 0

  URL_PATTERN.lastIndex = 0
  let match = URL_PATTERN.exec(value)
  while (match) {
    const token = match[0]
    const { urlText, trailing } = splitUrlToken(token)
    const normalizedUrl = normalizeMessageUrl(urlText)
    const startIndex = match.index
    const urlEndIndex = startIndex + urlText.length
    const tokenEndIndex = startIndex + token.length

    if (startIndex > lastIndex) {
      parts.push({ key: `text-${partIndex}`, text: value.slice(lastIndex, startIndex) })
      partIndex += 1
    }

    if (normalizedUrl) {
      parts.push({ key: `url-${partIndex}`, text: urlText, url: normalizedUrl })
      partIndex += 1
    } else {
      parts.push({ key: `text-${partIndex}`, text: urlText })
      partIndex += 1
    }

    if (trailing) {
      parts.push({ key: `text-${partIndex}`, text: trailing })
      partIndex += 1
    }

    lastIndex = tokenEndIndex
    URL_PATTERN.lastIndex = tokenEndIndex
    match = URL_PATTERN.exec(value)

    if (urlEndIndex === startIndex) break
  }

  if (lastIndex < value.length) {
    parts.push({ key: `text-${partIndex}`, text: value.slice(lastIndex) })
  }

  return parts.length ? parts : [{ key: 'text-0', text: value }]
}

const getFlyerDescriptionText = (description) => cleanText(description)

const getFlyerThreadTitle = (flyer = {}) =>
  cleanText(firstValue(flyer.title, getFlyerDescriptionText(flyer.description), flyer.category, 'Flyer thread'))

const normalizeKeyPart = (value) => String(value ?? '').trim().toLowerCase()

const formatChatTime = (dateValue) => {
  const value = cleanText(dateValue)
  if (!value || /\b(ago|just now|now)\b/i.test(value)) return ''

  const normalized = value.includes(' ') ? value.replace(' ', 'T') : value
  const parsed = new Date(normalized)
  if (!Number.isNaN(parsed.getTime())) {
    return new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit'
    }).format(parsed)
  }

  const timeMatch = value.match(/\b(\d{1,2}):(\d{2})(?::\d{2})?\s*([AP]M)?\b/i)
  if (!timeMatch) return ''

  let hours = Number(timeMatch[1])
  const minutes = Number(timeMatch[2])
  const period = timeMatch[3]?.toUpperCase()
  if (period === 'PM' && hours < 12) hours += 12
  if (period === 'AM' && hours === 12) hours = 0

  const date = new Date()
  date.setHours(hours, minutes, 0, 0)
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit'
  }).format(date)
}

const firstFormattedChatTime = (...values) => {
  for (const value of values) {
    const formatted = formatChatTime(value)
    if (formatted) return formatted
  }
  return ''
}

const formatConversationListTime = (conversation = {}) => {
  const dateValue = cleanText(conversation.lastDateValue)
  if (/\b(ago|just now|now)\b/i.test(dateValue)) return conversation.lastTime || ''
  if (!dateValue) return conversation.lastTime || ''

  const normalized = dateValue.includes(' ') ? dateValue.replace(' ', 'T') : dateValue
  const parsed = new Date(normalized)
  if (Number.isNaN(parsed.getTime())) return conversation.lastTime || ''
  if (isSameDay(parsed, new Date())) return formatChatTime(dateValue) || conversation.lastTime || ''
  return formatChatDate(parsed)
}

const resolvePeerId = (item = {}) => {
  const me = currentUserId()
  const fromId = String(firstNonZeroValue(item.from_id, item.sender_id, item.user_from, item.created_by, '') || '')
  const toId = String(firstNonZeroValue(item.to_id, item.receiver_id, item.user_to, item.follow_user_id, '') || '')
  const apiPeerId = String(firstNonZeroValue(
    item.other_user_id,
    item.chat_user_id,
    item.friend_id,
    item.user_id,
    item.userid,
    item.uid,
    ''
  ) || '')

  if (apiPeerId && apiPeerId !== me) return apiPeerId

  if (fromId && fromId === me && toId) return toId
  if (toId && toId === me && fromId) return fromId
  if (toId && toId !== me) return toId
  if (fromId && fromId !== me) return fromId
  return ''
}

const normalizeFlyer = (item = {}) => {
  const flyer = item.flyer && typeof item.flyer === 'object' ? item.flyer : item
  const image = cleanText(firstValue(flyer.flyer_image, flyer.image, flyer.image_url, flyer.image1, Array.isArray(flyer.images) ? flyer.images[0] : '', ''))

  return {
    id: firstNonZeroValue(flyer.flyer_id, flyer.last_flyer_id, flyer.id, item.flyer_id, item.last_flyer_id, '') || '0',
    title: decodeText(firstValue(flyer.flyer_title, flyer.title, flyer.name, '')),
    category: firstValue(flyer.category_name, flyer.catergory_name, flyer.cat_name, flyer.content_type_name, flyer.category, ''),
    description: decodeHtmlEntities(firstValue(flyer.flyer_description, flyer.description, flyer.desc, '')).trim(),
    image,
    location: firstValue(flyer.location, flyer.address, flyer.city, flyer.state, ''),
    address: firstValue(flyer.address, flyer.location, ''),
    status: firstValue(flyer.status_name, flyer.publish_status_name, ''),
    shareUrl: firstValue(flyer.share_url, flyer.shareUrl, '')
  }
}

const normalizeMessage = (item = {}, index = 0) => {
  const senderId = String(firstValue(item.from_id, item.sender_id, item.user_id, item.userid, ''))
  const mineValue = firstValue(item.mine, item.is_mine, item.own, '')
  const align = String(firstValue(item.align, '')).toLowerCase()
  const mine = align ? align === 'right' : mineValue === true || mineValue === 1 || mineValue === '1' || senderId === currentUserId()
  const dateValue = mine
    ? firstValue(item.send_created_date, item.created_at, item.created_date, item.date_time, item.datetime, item.time, '')
    : firstValue(item.rec_created_date, item.created_at, item.created_date, item.date_time, item.datetime, item.time, '')

  return {
    id: firstValue(item.id, item.chat_id, item.message_id, `message-${index}`),
    mine,
    body: cleanText(firstValue(item.message, item.msg, item.body, item.comment, item.text, '')),
    time: dateValue ? formatChatTime(dateValue) : '',
    dateValue
  }
}

const getMessageKey = (message = {}) => {
  const id = normalizeKeyPart(message.id)
  if (id && !id.startsWith('message-')) return `id:${id}`
  return [
    normalizeKeyPart(message.mine ? 'mine' : 'peer'),
    normalizeKeyPart(message.dateValue || message.time),
    normalizeKeyPart(message.body)
  ].join('|')
}

const sortMessagesOldestFirst = (messages = []) =>
  messages.slice().sort((a, b) => parseChatDate(a.dateValue).getTime() - parseChatDate(b.dateValue).getTime())

const uniqueMessages = (messages = []) => {
  const seen = new Set()
  return sortMessagesOldestFirst(messages.filter((message) => {
    if (!message?.body) return false
    const key = getMessageKey(message)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  }))
}

const normalizeThread = (item = {}, index = 0) => {
  const rawMessages = firstValue(item.messages, item.chat, item.chat_list, item.replies, [])
  const messages = Array.isArray(rawMessages) && rawMessages.length
    ? uniqueMessages(rawMessages.map(normalizeMessage))
    : [normalizeMessage(item, index)].filter((message) => message.body)
  const lastMessage = cleanText(firstValue(item.last_msg, item.last_message, item.message, item.msg, messages[messages.length - 1]?.body, ''))
  const dateValue = firstValue(item.last_created_date, item.last_time, item.updated_at, item.created_at, item.created_date, messages[messages.length - 1]?.dateValue, '')
  const dateMeta = getDateMeta(parseChatDate(dateValue))
  const flyer = normalizeFlyer(item)

  return {
    id: String(firstNonZeroValue(item.thread_id, item.flyer_chat_id, item.flyer_id, flyer.id, item.last_flyer_id, '') || `thread-${index}`),
    flyer,
    lastMessage,
    lastTime: firstFormattedChatTime(dateValue, item.lastTime, item.last_time_label),
    lastDateValue: dateValue,
    unread: Number(firstValue(item.unread, item.unread_count, 0)) || 0,
    dateKey: dateMeta.key,
    dateLabel: dateMeta.label,
    dateSortValue: dateMeta.sortValue,
    messages
  }
}

const hasMessageContent = (message = {}) => Boolean(cleanText(message.body))

const hasThreadContent = (thread = {}) =>
  Boolean(cleanText(thread.lastMessage)) ||
  (Array.isArray(thread.messages) && thread.messages.some(hasMessageContent))

const getThreadKey = (thread = {}) =>
  String(firstNonZeroValue(
    thread.id,
    thread.thread_id,
    thread.flyer_chat_id,
    thread.flyer?.id,
    thread.flyer_id,
    thread.last_flyer_id
  ) || [
    normalizeKeyPart(thread.lastMessage),
    normalizeKeyPart(thread.lastTime),
    normalizeKeyPart(thread.flyer?.title)
  ].join('|'))

const uniqueThreads = (threads = []) => {
  const merged = new Map()

  threads.filter(hasThreadContent).forEach((thread) => {
    const key = getThreadKey(thread)
    if (!merged.has(key)) {
      merged.set(key, {
        ...thread,
        messages: uniqueMessages(Array.isArray(thread.messages) ? thread.messages : [])
      })
      return
    }

    const existing = merged.get(key)
    existing.messages = uniqueMessages([
      ...(Array.isArray(existing.messages) ? existing.messages : []),
      ...(Array.isArray(thread.messages) ? thread.messages : [])
    ])
    existing.lastMessage = cleanText(thread.lastMessage) || existing.lastMessage
    existing.lastTime = thread.lastTime || existing.lastTime
    existing.lastDateValue = thread.lastDateValue || existing.lastDateValue
    existing.dateKey = thread.dateKey || existing.dateKey
    existing.dateLabel = thread.dateLabel || existing.dateLabel
    existing.dateSortValue = thread.dateSortValue || existing.dateSortValue
    existing.unread = Math.max(Number(existing.unread) || 0, Number(thread.unread) || 0)
    existing.flyer = {
      ...(existing.flyer || {}),
      ...(thread.flyer || {})
    }
  })

  return Array.from(merged.values())
}

const normalizeConversation = (item = {}, index = 0) => {
  const peerId = resolvePeerId(item)
  if (!peerId) return null
  const threadRows = firstValue(item.flyerThreads, item.threads, item.flyers, item.items, [])
  const isApiConversationRow = firstNonZeroValue(item.user_id, item.userid, item.to_id, item.from_id, '') &&
    firstValue(item.user_name, item.to_name, item.to_user_name, item.receiver_name, item.name, item.username, '') &&
    firstValue(item.last_msg, item.last_message, item.message, item.msg, '')
  const flyerThreads = Array.isArray(threadRows) && threadRows.length
    ? uniqueThreads(threadRows.map(normalizeThread))
    : (isApiConversationRow ? [] : (firstValue(item.flyer_id, item.message, item.msg, '') ? [normalizeThread(item, index)] : []))
  const contactName = cleanText(firstValue(
    item.to_name,
    item.to_user_name,
    item.receiver_name,
    item.user_name,
    item.name,
    item.username,
    item.user_username,
    item.full_name,
    item.from_name,
    item.sender_name,
    flyerThreads[0]?.flyer?.author,
    ''
  )) || 'Chat'

  const lastDateValue = firstValue(item.last_created_date, item.last_time, item.created_date, flyerThreads[0]?.lastDateValue, '')

  return {
    id: String(peerId),
    contact: {
      id: String(peerId),
      name: contactName,
      status: firstValue(item.status_text, item.online_status, item.last_seen, ''),
      image: normalizeContactImage(firstValue(item.user_img, item.user_image, item.profile_image, item.profile_pic, ''))
    },
    lastTime: firstFormattedChatTime(lastDateValue, item.lastTime, item.last_time_label),
    lastDateValue,
    sortDateValue: lastDateValue,
    unread: Number(firstValue(item.unread, item.unread_count, item.chat_count, 0)) || 0,
    lastMessage: cleanText(firstValue(item.last_msg, item.last_message, item.message, item.msg, flyerThreads[0]?.lastMessage, '')),
    flyerThreads
  }
}

const hasConversationContent = (conversation = {}) =>
  Boolean(cleanText(conversation.lastMessage)) ||
  (Array.isArray(conversation.flyerThreads) && conversation.flyerThreads.some(hasThreadContent))

const hasRealConversationContact = (conversation = {}) => {
  const contact = getConversationContact(conversation)
  const contactId = String(contact.id || '').trim()
  const contactName = cleanText(contact.name)

  if (!contactId || contactId === currentUserId() || contactId.startsWith('chat-')) return false
  return Boolean(contact.image || (contactName && contactName.toLowerCase() !== 'chat'))
}

const buildTemporaryConversation = (target = {}) => {
  const toId = String(firstNonZeroValue(target.toId, target.to_id, '') || '').trim()
  if (!toId || toId === currentUserId()) return null

  const contactName = cleanText(target.name) || 'Chat'
  const flyerId = String(firstNonZeroValue(target.flyerId, target.flyer_id, '') || '').trim()
  const temporaryThread = flyerId && target.source === 'flyer'
    ? {
        ...emptyThread,
        id: flyerId,
        flyer: {
          ...emptyFlyer,
          id: flyerId,
          title: target.flyerTitle || '',
          description: target.flyerDescription || '',
          image: target.flyerImage || '',
          category: target.flyerCategory || 'Flyer'
        },
        messages: []
      }
    : null

  return {
    id: toId,
    pending: true,
    contact: {
      id: toId,
      name: contactName,
      status: '',
      image: normalizeContactImage(target.image)
    },
    lastTime: '',
    lastDateValue: '',
    sortDateValue: '',
    unread: 0,
    lastMessage: '',
    flyerThreads: temporaryThread ? [temporaryThread] : []
  }
}

const ensureTemporaryTargetConversation = (target = {}) => {
  const temporaryConversation = buildTemporaryConversation(target)
  if (!temporaryConversation || !hasRealConversationContact(temporaryConversation)) return ''

  const existingIndex = conversations.value.findIndex((conversation) =>
    String(conversation.contact?.id || conversation.id || '') === String(temporaryConversation.contact.id)
  )

  if (existingIndex >= 0) {
    conversations.value[existingIndex] = {
      ...conversations.value[existingIndex],
      contact: {
        ...conversations.value[existingIndex].contact,
        name: temporaryConversation.contact.name || conversations.value[existingIndex].contact.name,
        image: temporaryConversation.contact.image || conversations.value[existingIndex].contact.image
      }
    }
    return String(conversations.value[existingIndex].id || temporaryConversation.id)
  }

  conversations.value = [temporaryConversation, ...conversations.value]
  return String(temporaryConversation.id)
}

const uniqueConversations = (items = []) => {
  const merged = new Map()

  items.filter((conversation) => conversation?.contact?.id && hasConversationContent(conversation)).forEach((conversation) => {
    const key = String(conversation.contact.id)
    if (!merged.has(key)) {
      merged.set(key, {
        ...conversation,
        flyerThreads: uniqueThreads(conversation.flyerThreads || [])
      })
      return
    }

    const existing = merged.get(key)
    existing.contact = {
      ...existing.contact,
      ...conversation.contact,
      name: conversation.contact.name === 'Chat' ? existing.contact.name : conversation.contact.name,
      image: conversation.contact.image || existing.contact.image
    }
    existing.flyerThreads = uniqueThreads([
      ...(existing.flyerThreads || []),
      ...(conversation.flyerThreads || [])
    ])
    existing.lastMessage = cleanText(conversation.lastMessage) || existing.lastMessage
    existing.lastTime = conversation.lastTime || existing.lastTime
    existing.lastDateValue = conversation.lastDateValue || existing.lastDateValue
    existing.sortDateValue = conversation.sortDateValue || existing.sortDateValue || conversation.lastDateValue || existing.lastDateValue
    existing.unread = Math.max(Number(existing.unread) || 0, Number(conversation.unread) || 0)
  })

  return Array.from(merged.values())
}

const buildThreadsFromDetailRows = (rows = []) => {
  const groupedRows = new Map()

  rows.forEach((row, index) => {
    if (!hasFlyerThreadData(row)) return

    const threadRows = firstValue(row.flyerThreads, row.threads, row.flyers, row.items, null)
    if (Array.isArray(threadRows)) {
      threadRows
        .filter(hasFlyerThreadData)
        .forEach((thread, threadIndex) => groupedRows.set(`nested-${index}-${threadIndex}`, [thread]))
      return
    }

    const flyer = normalizeFlyer(row)
    const key = String(firstValue(row.thread_id, row.flyer_chat_id, row.flyer_id, flyer.id, `thread-${index}`))
    if (!groupedRows.has(key)) groupedRows.set(key, [])
    groupedRows.get(key).push(row)
  })

  return uniqueThreads(Array.from(groupedRows.entries()).map(([key, items], index) => {
    if (items.length === 1 && Array.isArray(firstValue(items[0].messages, items[0].chat, items[0].chat_list, []))) {
      return normalizeThread(items[0], index)
    }

    const messages = uniqueMessages(items.map(normalizeMessage))
    const newest = items[items.length - 1] || {}
    const flyerInfoRow = items.find((item) =>
      firstValue(item.flyer_image, item.flyer_title, item.flyer_description, item.share_url, '')
    ) || newest
    const dateValue = firstValue(newest.last_created_date, newest.created_at, newest.created_date, newest.date_time, newest.datetime, newest.time, '')
    const dateMeta = getDateMeta(parseChatDate(dateValue))

    return {
      ...normalizeThread(flyerInfoRow, index),
      id: key,
      lastMessage: cleanText(firstValue(newest.last_msg, newest.last_message, newest.message, newest.msg, messages[messages.length - 1]?.body, '')),
      lastTime: dateValue ? formatChatTime(dateValue) : '',
      lastDateValue: dateValue,
      dateKey: dateMeta.key,
      dateLabel: dateMeta.label,
      dateSortValue: dateMeta.sortValue,
      messages
    }
  }))
}

const extractDetailMessages = (rows = []) =>
  uniqueMessages(rows
    .filter((row) => !hasFlyerThreadData(row))
    .flatMap((row, index) => {
      const nestedMessages = firstValue(row.messages, row.chat, row.chat_list, row.replies, null)
      if (Array.isArray(nestedMessages)) {
        return nestedMessages.map((message, messageIndex) => normalizeMessage(message, `${index}-${messageIndex}`))
      }
      return normalizeMessage(row, index)
    })
    .filter((message) => message.body))

const conversations = ref([])
const activeConversationId = ref('')
const activeFlyerThreadId = ref('')
const detailMessages = ref([])
const compactChatOpen = ref(false)
const chatSocket = ref(null)
const chatSocketUserId = ref('')
const chatRealtimeConnected = ref(false)
const activeChatUserIds = ref(new Set())
const typingUserIds = ref(new Set())
const activeChatTarget = ref(getRouteChatTarget())
clearStoredChatTarget()
let incomingSoundContext = null
let lastIncomingSoundAt = 0
let typingStopTimer = null
let lastTypingEmitAt = 0
let localTypingActive = false
const chatSocketListeners = []
const typingExpiryTimers = new Map()
const isFlyerThreadMode = computed(() => Boolean(activeChatTarget.value.flyerId && activeChatTarget.value.source === 'flyer'))
const isUserChatMode = computed(() => activeChatTarget.value.source === 'user')
const hasActiveConversation = computed(() => Boolean(activeConversationId.value))
const chatRealtimeStatusLabel = computed(() => chatRealtimeConnected.value ? 'Active' : 'Inactive')
const chatRealtimeStatusClass = computed(() => chatRealtimeConnected.value
  ? 'bg-emerald-50 text-emerald-700'
  : 'bg-slate-100 text-slate-500'
)
const chatRealtimeDotClass = computed(() => chatRealtimeConnected.value ? 'bg-emerald-500' : 'bg-slate-400')
const isConversationRealtimeActive = (conversation = {}) =>
  activeChatUserIds.value.has(String(getConversationContact(conversation).id || conversation.id || '')) ||
  (chatRealtimeConnected.value && String(conversation.id || '') === String(activeConversationId.value || ''))
const getConversationRealtimeDotClass = (conversation = {}) =>
  isConversationRealtimeActive(conversation) ? 'bg-emerald-500' : 'bg-slate-400'

const clearFlyerChatTargetAfterFirstSend = () => {
  if (!isFlyerThreadMode.value) return
  activeChatTarget.value = {
    ...activeChatTarget.value,
    flyerId: '',
    flyerTitle: '',
    flyerDescription: '',
    flyerImage: '',
    flyerCategory: '',
    source: 'user'
  }
  activeFlyerThreadId.value = ''
}

const activeConversation = computed(() =>
  conversations.value.find((conversation) => conversation.id === activeConversationId.value) || emptyConversation
)

const activeFlyerThread = computed(() => {
  if (isUserChatMode.value) return emptyThread
  const threads = Array.isArray(activeConversation.value.flyerThreads) ? activeConversation.value.flyerThreads : []
  return threads.find((thread) => thread.id === activeFlyerThreadId.value) || threads[0] || emptyThread
})

const getConversationContact = (conversation) => conversation?.contact || emptyConversation.contact
const getThreadFlyer = (thread) => thread?.flyer || emptyFlyer
const getThreadMessages = (thread) => Array.isArray(thread?.messages) ? sortMessagesOldestFirst(thread.messages) : []
const getFlyerPlaceholderLetter = (flyer = {}) => {
  const label = cleanText(firstValue(flyer.title, flyer.category, flyer.description, 'Flyer'))
  return (label.charAt(0) || 'F').toUpperCase()
}
const activeContact = computed(() => getConversationContact(activeConversation.value))
const activePeerTyping = computed(() => typingUserIds.value.has(String(activeContact.value?.id || activeConversationId.value || '')))

const playIncomingMessageSound = () => {
  if (typeof window === 'undefined') return
  const now = Date.now()
  if (now - lastIncomingSoundAt < 700) return
  lastIncomingSoundAt = now

  try {
    const AudioContextCtor = window.AudioContext || window.webkitAudioContext
    if (!AudioContextCtor) return
    incomingSoundContext = incomingSoundContext || new AudioContextCtor()
    const context = incomingSoundContext
    const startAt = context.currentTime
    const gain = context.createGain()
    const oscillator = context.createOscillator()

    oscillator.type = 'sine'
    oscillator.frequency.setValueAtTime(740, startAt)
    oscillator.frequency.exponentialRampToValueAtTime(980, startAt + 0.08)
    gain.gain.setValueAtTime(0.0001, startAt)
    gain.gain.exponentialRampToValueAtTime(0.18, startAt + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.22)

    oscillator.connect(gain)
    gain.connect(context.destination)
    oscillator.start(startAt)
    oscillator.stop(startAt + 0.24)
  } catch {
    // Browsers can block audio until the user interacts with the page.
  }
}

const shouldPlayIncomingMessageSound = (payload = {}) => {
  const currentId = String(currentUserId() || '').trim()
  const senderId = getSocketSenderId(payload)

  return !senderId || !currentId || senderId !== currentId
}

const extractSocketPayload = (payload = {}) => payload?.data && typeof payload.data === 'object' ? payload.data : payload

const extractSocketMessagePayload = (payload = {}) => {
  const response = payload?.socket_response && typeof payload.socket_response === 'object'
    ? payload.socket_response
    : payload
  if (Array.isArray(response)) return response[0] || {}
  if (Array.isArray(response?.data)) return response.data[0] || {}
  if (response?.data && typeof response.data === 'object') return response.data
  return response || {}
}

const getSocketSenderId = (payload = {}) => {
  const source = extractSocketMessagePayload(payload)
  const message = source.message && typeof source.message === 'object' ? source.message : {}
  return String(firstNonZeroValue(
    source.from_id,
    source.sender_id,
    source.userid,
    source.user_id,
    source.senderId,
    message.from_id,
    message.sender_id,
    message.userid,
    message.user_id,
    ''
  ) || '').trim()
}

const isSocketMessageMine = (payload = {}) => {
  const source = extractSocketMessagePayload(payload)
  const message = source.message && typeof source.message === 'object' ? source.message : {}
  const align = String(firstValue(source.align, message.align, '')).toLowerCase()
  if (align) return align === 'right'

  const mineValue = firstValue(source.mine, source.is_mine, source.own, message.mine, message.is_mine, message.own, '')
  if (mineValue === true || mineValue === 1 || mineValue === '1') return true
  return getSocketSenderId(payload) === String(currentUserId() || '')
}

const getSocketPeerId = (payload = {}) => {
  const source = extractSocketMessagePayload(payload)
  const message = source.message && typeof source.message === 'object' ? source.message : {}
  const me = String(currentUserId() || '').trim()
  const fromId = String(firstNonZeroValue(
    source.from_id,
    source.sender_id,
    source.userid,
    source.user_id,
    source.senderId,
    message.from_id,
    message.sender_id,
    message.userid,
    message.user_id,
    ''
  ) || '').trim()
  const toId = String(firstNonZeroValue(
    source.to_id,
    source.receiver_id,
    source.toId,
    message.to_id,
    message.receiver_id,
    message.toId,
    ''
  ) || '').trim()

  if (fromId && fromId !== me) return fromId
  if (toId && toId !== me) return toId
  return ''
}

const getSocketMessageText = (payload = {}) => {
  const source = extractSocketMessagePayload(payload)
  const message = source.message && typeof source.message === 'object' ? source.message : {}
  return cleanText(firstValue(
    source.message_text,
    source.msg,
    source.body,
    typeof source.message === 'string' ? source.message : '',
    message.message,
    message.msg,
    message.body,
    ''
  ))
}

const getSocketMessageDateValue = (payload = {}, mine = isSocketMessageMine(payload)) => {
  const source = extractSocketMessagePayload(payload)
  const message = source.message && typeof source.message === 'object' ? source.message : {}
  return firstValue(
    mine ? source.send_created_date : source.rec_created_date,
    mine ? message.send_created_date : message.rec_created_date,
    source.created_at,
    source.created_date,
    source.date_time,
    source.datetime,
    source.time,
    message.created_at,
    message.created_date,
    message.date_time,
    message.datetime,
    message.time,
    new Date().toISOString()
  )
}

const getSocketMessageId = (payload = {}) => {
  const source = extractSocketMessagePayload(payload)
  const message = source.message && typeof source.message === 'object' ? source.message : {}
  return firstValue(
    source.id,
    source.chat_id,
    source.message_id,
    message.id,
    message.chat_id,
    message.message_id,
    ''
  )
}

const getSocketFlyerId = (payload = {}) => {
  const source = extractSocketMessagePayload(payload)
  const message = source.message && typeof source.message === 'object' ? source.message : {}
  return String(firstNonZeroValue(
    source.flyer_id,
    source.last_flyer_id,
    message.flyer_id,
    message.last_flyer_id,
    ''
  ) || '').trim()
}

const mergeSocketMessage = (messages = [], socketMessage = {}) => {
  const pendingIndex = messages.findIndex((message) =>
    message.pending &&
    message.mine === socketMessage.mine &&
    message.body === socketMessage.body
  )
  const nextMessages = messages.slice()
  if (pendingIndex >= 0) nextMessages.splice(pendingIndex, 1, socketMessage)
  else nextMessages.push(socketMessage)
  return uniqueMessages(nextMessages)
}

const replaceOptimisticMessage = (messages = [], optimisticId, confirmedMessage = {}) => {
  const confirmedId = String(confirmedMessage.id || '')
  const remainingMessages = messages.filter((message) =>
    message.id !== optimisticId && (!confirmedId || String(message.id || '') !== confirmedId)
  )
  return uniqueMessages([...remainingMessages, confirmedMessage])
}

const appendActiveConversationSocketMessage = (payload = {}) => {
  const peerId = getSocketPeerId(payload)
  const activePeerId = String(activeConversation.value?.contact?.id || activeConversationId.value || '')
  const body = getSocketMessageText(payload)
  if (!peerId || peerId !== activePeerId || !body) return

  const senderId = getSocketSenderId(payload)
  const mine = isSocketMessageMine(payload)
  const dateValue = getSocketMessageDateValue(payload, mine)
  const socketMessage = {
    id: getSocketMessageId(payload) || `socket-${senderId || peerId}-${dateValue}-${body}`,
    mine,
    body,
    time: formatChatTime(dateValue),
    dateValue
  }
  const flyerId = getSocketFlyerId(payload)
  const matchingThread = flyerId
    ? activeFlyerThreads.value.find((thread) => String(getThreadFlyer(thread).id || thread.id) === flyerId)
    : null

  if (matchingThread) {
    matchingThread.messages = mergeSocketMessage(matchingThread.messages || [], socketMessage)
    matchingThread.lastMessage = body
    matchingThread.lastTime = socketMessage.time
    matchingThread.lastDateValue = dateValue
    matchingThread.unread = 0
  } else {
    detailMessages.value = mergeSocketMessage(detailMessages.value, socketMessage)
  }
  scrollMessagesToBottom()
}

const updateConversationFromSocketPayload = (payload = {}) => {
  const peerId = getSocketPeerId(payload)
  if (!peerId) return false
  const conversationIndex = conversations.value.findIndex((item) =>
    String(getConversationContact(item).id || item.id || '') === String(peerId)
  )
  const conversation = conversations.value[conversationIndex]
  if (!conversation) return false

  const messageText = getSocketMessageText(payload)
  const fromCurrentUser = isSocketMessageMine(payload)
  const dateValue = getSocketMessageDateValue(payload, fromCurrentUser)
  const isActiveConversation = String(getConversationContact(conversation).id || conversation.id || '') ===
    String(activeConversation.value?.contact?.id || activeConversationId.value || '')
  const shouldCountUnread = !fromCurrentUser && !isActiveConversation
  const unread = isActiveConversation
    ? 0
    : shouldCountUnread
      ? (Number(conversation.unread) || 0) + 1
      : Number(conversation.unread) || 0

  conversations.value[conversationIndex] = {
    ...conversation,
    lastMessage: messageText || conversation.lastMessage,
    lastDateValue: dateValue,
    sortDateValue: dateValue,
    lastTime: firstFormattedChatTime(dateValue, conversation.lastTime),
    unread
  }
  return true
}

const normalizeActiveUserIds = (payload = {}) => {
  const source = extractSocketPayload(payload)
  const rows = Array.isArray(source)
    ? source
    : firstValue(source.userIds, source.user_ids, source.users, source.active_users, source.online_users, [])
  if (!Array.isArray(rows)) return []

  return rows
    .map((item) => String(firstNonZeroValue(
      typeof item === 'object' ? item.user_id : item,
      typeof item === 'object' ? item.userid : '',
      typeof item === 'object' ? item.id : '',
      ''
    ) || '').trim())
    .filter(Boolean)
    .filter((id) => id !== String(currentUserId() || ''))
}

const setActiveChatUsers = (payload = {}) => {
  activeChatUserIds.value = new Set(normalizeActiveUserIds(payload))
}

const setChatUserActive = (payload = {}, active = true) => {
  const userId = String(firstNonZeroValue(
    typeof payload === 'object' ? payload.user_id : payload,
    typeof payload === 'object' ? payload.userid : '',
    typeof payload === 'object' ? payload.id : '',
    typeof payload === 'object' ? payload.userId : '',
    ''
  ) || '').trim()
  if (!userId || userId === String(currentUserId() || '')) return

  const nextIds = new Set(activeChatUserIds.value)
  if (active) nextIds.add(userId)
  else nextIds.delete(userId)
  activeChatUserIds.value = nextIds
}

const getTypingUserId = (payload = {}) => {
  const source = extractSocketPayload(payload)
  return String(firstNonZeroValue(
    source.from_id,
    source.sender_id,
    source.user_id,
    source.userid,
    source.userId,
    source.id,
    ''
  ) || '').trim()
}

const setChatUserTyping = (payload = {}, typing = true) => {
  const source = extractSocketPayload(payload)
  const userId = getTypingUserId(source)
  const toId = String(firstNonZeroValue(source.to_id, source.receiver_id, source.toId, '') || '').trim()
  const me = String(currentUserId() || '').trim()
  if (!userId || userId === me || (toId && toId !== me)) return

  if (typingExpiryTimers.has(userId)) {
    clearTimeout(typingExpiryTimers.get(userId))
    typingExpiryTimers.delete(userId)
  }

  const nextIds = new Set(typingUserIds.value)
  if (typing) {
    nextIds.add(userId)
    typingExpiryTimers.set(userId, setTimeout(() => {
      const expiredIds = new Set(typingUserIds.value)
      expiredIds.delete(userId)
      typingUserIds.value = expiredIds
      typingExpiryTimers.delete(userId)
    }, 2500))
  } else {
    nextIds.delete(userId)
  }
  typingUserIds.value = nextIds
}

const handleSocketMessageEvent = (eventName, payload = {}) => {
  setChatUserTyping(payload, false)
  const selectedUserId = String(activeConversation.value?.contact?.id || activeConversationId.value || '')
  if (selectedUserId && getSocketPeerId(payload) === selectedUserId) {
    console.log(`[Chats socket:${eventName}][selected user:${selectedUserId}]`, payload)
  }
  if (shouldPlayIncomingMessageSound(payload)) {
    playIncomingMessageSound()
  }
  const updatedConversation = updateConversationFromSocketPayload(payload)
  if (!updatedConversation) {
    loadConversations({ preserveActive: true, silent: true })
  }
  appendActiveConversationSocketMessage(payload)
}

const addChatSocketListener = (socket, eventName, handler) => {
  socket.on(eventName, handler)
  chatSocketListeners.push({ socket, eventName, handler })
}

const disconnectChatSocket = () => {
  if (typingStopTimer) {
    clearTimeout(typingStopTimer)
    typingStopTimer = null
  }
  localTypingActive = false
  lastTypingEmitAt = 0
  chatRealtimeConnected.value = false
  activeChatUserIds.value = new Set()
  typingUserIds.value = new Set()
  chatSocketListeners.splice(0).forEach(({ socket, eventName, handler }) => socket.off(eventName, handler))
  typingExpiryTimers.forEach((timer) => clearTimeout(timer))
  typingExpiryTimers.clear()
  chatSocket.value = null
  chatSocketUserId.value = ''
}

const cleanupChatRealtime = () => {
  disconnectChatSocket()
  incomingSoundContext?.close?.()
  incomingSoundContext = null
}

const handleSessionLogout = () => {
  disconnectChatSocket()
  activeConversationId.value = ''
  activeFlyerThreadId.value = ''
  detailMessages.value = []
  conversations.value = []
}

const refreshActiveConversationFromSocket = () => {
  const toId = String(activeConversation.value?.contact?.id || activeConversationId.value || '').trim()
  if (!currentUserId() || !toId) return
  loadConversationDetail(toId, { silent: true })
}

const connectChatSocket = () => {
  const userId = String(currentUserId() || '').trim()
  if (!userId) {
    disconnectChatSocket()
    return
  }

  if (chatSocket.value && chatSocketUserId.value === userId) return

  disconnectChatSocket()
  chatSocketUserId.value = userId
  const socket = connectChatRealtime(userId)
  if (!socket) return

  const handleConnect = () => {
    chatRealtimeConnected.value = true
    refreshActiveConversationFromSocket()
  }
  const handleDisconnect = () => {
    chatRealtimeConnected.value = false
  }
  const handleConnectError = () => {
    chatRealtimeConnected.value = false
  }
  addChatSocketListener(socket, 'connect', handleConnect)
  addChatSocketListener(socket, 'disconnect', handleDisconnect)
  addChatSocketListener(socket, 'connect_error', handleConnectError)
  ;['message', 'receive_message', 'new_message', 'chat_message', 'send_message'].forEach((eventName) => {
    addChatSocketListener(socket, eventName, (payload) => handleSocketMessageEvent(eventName, payload))
  })
  ;['active_users', 'online_users'].forEach((eventName) => {
    addChatSocketListener(socket, eventName, setActiveChatUsers)
  })
  ;['user_active', 'user_online'].forEach((eventName) => {
    addChatSocketListener(socket, eventName, (payload) => setChatUserActive(payload, true))
  })
  ;['user_inactive', 'user_offline'].forEach((eventName) => {
    addChatSocketListener(socket, eventName, (payload) => setChatUserActive(payload, false))
  })
  ;['typing_start', 'typing', 'user_typing'].forEach((eventName) => {
    addChatSocketListener(socket, eventName, (payload) => {
      const source = extractSocketPayload(payload)
      setChatUserTyping(source, source.typing !== false && source.is_typing !== false)
    })
  })
  ;['typing_stop', 'stop_typing', 'user_stopped_typing'].forEach((eventName) => {
    addChatSocketListener(socket, eventName, (payload) => setChatUserTyping(payload, false))
  })

  chatSocket.value = socket
  chatRealtimeConnected.value = socket.connected
  if (socket.connected) refreshActiveConversationFromSocket()
}

const getConversationSortTime = (conversation = {}) => {
  const values = [
    conversation.sortDateValue,
    conversation.lastDateValue,
    ...(Array.isArray(conversation.flyerThreads)
      ? conversation.flyerThreads.flatMap((thread) => [thread.lastDateValue, thread.dateSortValue])
      : [])
  ]

  for (const value of values) {
    if (!value) continue
    if (typeof value === 'number' && Number.isFinite(value)) return value
    const normalized = String(value).includes(' ') ? String(value).replace(' ', 'T') : String(value)
    const parsed = new Date(normalized)
    if (!Number.isNaN(parsed.getTime())) return parsed.getTime()
  }

  return 0
}

const sortConversationsByLatest = (items = []) =>
  items.slice().sort((a, b) => {
    return getConversationSortTime(b) - getConversationSortTime(a)
  })

const visibleConversations = computed(() => {
  return sortConversationsByLatest(conversations.value.filter((conversation) =>
    (conversation.pending || hasConversationContent(conversation)) &&
    hasRealConversationContact(conversation)
  ))
})

const filteredConversations = computed(() => {
  const query = search.value.trim().toLowerCase()
  if (!query) return visibleConversations.value

  return visibleConversations.value.filter((conversation) =>
    [
      getConversationContact(conversation).name,
      conversation.lastMessage,
      ...(Array.isArray(conversation.flyerThreads) ? conversation.flyerThreads : []).flatMap((thread) => [
        getThreadFlyer(thread).title,
        getThreadFlyer(thread).category,
        getThreadFlyer(thread).location,
        thread.lastMessage
      ])
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(query)
  )
})

const activeFlyerThreads = computed(() =>
  Array.isArray(activeConversation.value?.flyerThreads) ? activeConversation.value.flyerThreads : []
)

const filteredActiveFlyerThreads = computed(() => {
  const flyerId = activeChatTarget.value.flyerId
  const sourceThreads = isFlyerThreadMode.value && flyerId
    ? activeFlyerThreads.value.filter((thread) => String(getThreadFlyer(thread).id || thread.id) === String(flyerId))
    : activeFlyerThreads.value
  const query = search.value.trim().toLowerCase()
  if (!query) return sourceThreads

  return sourceThreads.filter((thread) =>
    [
      getThreadFlyer(thread).title,
      getThreadFlyer(thread).category,
      getThreadFlyer(thread).location,
      thread.lastMessage
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(query)
  )
})

const normalChatMessages = computed(() =>
  sortMessagesOldestFirst(detailMessages.value.filter((message) => message.body))
)

const getMessageBodyText = (item = {}) => String(item.message?.body || '')
const isLongMessage = (item = {}) => getMessageBodyText(item).length > MESSAGE_PREVIEW_LIMIT
const isMessageExpanded = (messageId) => expandedMessageIds.value.has(String(messageId))

const getVisibleMessageBody = (item = {}) => {
  const body = getMessageBodyText(item)
  if (!isLongMessage(item) || isMessageExpanded(item.id)) return body
  return `${body.slice(0, MESSAGE_PREVIEW_LIMIT).trimEnd()}...`
}

const getVisibleMessageBodyParts = (item = {}) => splitMessageBodyParts(getVisibleMessageBody(item))

const toggleMessageExpanded = (messageId) => {
  const key = String(messageId)
  const nextExpanded = new Set(expandedMessageIds.value)
  if (nextExpanded.has(key)) {
    nextExpanded.delete(key)
  } else {
    nextExpanded.add(key)
  }
  expandedMessageIds.value = nextExpanded
}

const getFlyerTimelineKey = (item = {}) => {
  if (!item.flyer) return ''
  const flyerId = firstNonZeroValue(item.flyer.id, item.flyer.flyer_id, item.flyer.last_flyer_id, item.threadId, '')
  const dateKey = getDateMeta(parseChatDate(item.dateValue)).key
  return `${flyerId || normalizeKeyPart(item.flyer.title || item.flyer.category)}|${dateKey}`
}

const chatTimelineItems = computed(() => {
  const sortedItems = sortMessagesOldestFirst([
    ...normalChatMessages.value.map((message) => ({
      id: `normal-${getMessageKey(message)}`,
      message,
      flyer: null,
      threadId: '',
      dateValue: message.dateValue
    })),
    ...filteredActiveFlyerThreads.value.flatMap((thread) =>
      getThreadMessages(thread).map((message) => ({
        id: `thread-${getThreadKey(thread)}-${getMessageKey(message)}`,
        message,
        flyer: getThreadFlyer(thread),
        threadId: thread.id,
        dateValue: message.dateValue || thread.dateKey
      }))
    )
  ])

  return sortedItems.map((item, index) => {
    const flyerKey = getFlyerTimelineKey(item)
    const previousFlyerKey = getFlyerTimelineKey(sortedItems[index - 1])
    return {
      ...item,
      showFlyerContext: Boolean(flyerKey && flyerKey !== previousFlyerKey)
    }
  })
})

const groupedChatTimeline = computed(() => {
  const groups = new Map()

  chatTimelineItems.value.forEach((item) => {
    const dateMeta = getDateMeta(parseChatDate(item.dateValue))
    if (!groups.has(dateMeta.key)) {
      groups.set(dateMeta.key, {
        key: dateMeta.key,
        label: dateMeta.label,
        sortValue: dateMeta.sortValue,
        items: []
      })
    }
    groups.get(dateMeta.key).items.push(item)
  })

  return Array.from(groups.values()).sort((a, b) => a.sortValue - b.sortValue)
})

const getInitials = (name) =>
  String(name || '')
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('')

const formatContactName = (name) => {
  const value = cleanText(name) || 'Chat'
  if (value.toLowerCase() === 'chat') return 'Chat'

  return value
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ')
}

const activeInitials = computed(() => getInitials(activeContact.value.name))

const getThreadCount = (conversation) =>
  Array.isArray(conversation?.flyerThreads) ? conversation.flyerThreads.length : 0

const scrollMessagesToBottom = async () => {
  await nextTick()
  if (!messagePane.value) return
  messagePane.value.scrollTop = messagePane.value.scrollHeight
}

const loadConversationDetail = async (conversationId, { silent = false } = {}) => {
  const userid = currentUserId()
  const conversation = conversations.value.find((item) => item.id === String(conversationId))
  const toId = conversation?.contact?.id || conversationId
  if (!userid || !toId) return

  if (!silent) {
    detailLoading.value = true
    detailMessages.value = []
  }
  detailError.value = ''

  try {
    const { data } = await api.getChatsFlyerUserView({ userid, to_id: toId })
    const rows = extractDetailRows(data)
    const threads = buildThreadsFromDetailRows(rows)
    detailMessages.value = extractDetailMessages(rows)
    if (rows.length) {
      conversation.pending = false
    }
    const peerRow = rows.find((row) => firstValue(row.user_name, row.user_img, row.user_id, '')) || {}
    if (peerRow.user_name || peerRow.user_img || peerRow.user_id) {
      conversation.contact = {
        ...conversation.contact,
        id: String(firstValue(peerRow.user_id, conversation.contact.id)),
        name: cleanText(firstValue(peerRow.user_name, conversation.contact.name)) || 'Chat',
        image: normalizeContactImage(firstValue(peerRow.user_img, conversation.contact.image)),
        status: conversation.contact.status
      }
    }
    if (threads.length) {
      conversation.flyerThreads = threads
    }
    const latestNormalMessage = detailMessages.value[detailMessages.value.length - 1]
    conversation.lastMessage = latestNormalMessage?.body || threads[0]?.lastMessage || conversation.lastMessage
    conversation.lastTime = latestNormalMessage?.time || threads[0]?.lastTime || conversation.lastTime
    conversation.lastDateValue = latestNormalMessage?.dateValue || threads[0]?.lastDateValue || conversation.lastDateValue
    const targetFlyerId = activeChatTarget.value.flyerId
    const targetThread = isFlyerThreadMode.value && targetFlyerId
      ? threads.find((thread) => String(getThreadFlyer(thread).id || thread.id) === String(targetFlyerId))
      : null
    activeFlyerThreadId.value = isUserChatMode.value ? '' : targetThread?.id || threads[0]?.id || ''
  } catch (error) {
    detailError.value = conversation?.pending
      ? ''
      : error?.response?.data?.message || error?.message || 'Chat detail could not be loaded.'
  } finally {
    if (!silent) {
      detailLoading.value = false
    }
    scrollMessagesToBottom()
  }
}

const selectConversation = (conversationId) => {
  stopTyping()
  if (String(conversationId) !== String(activeChatTarget.value.toId || '')) {
    activeChatTarget.value = { toId: '', flyerId: '', source: '' }
  }

  activeConversationId.value = conversationId
  compactChatOpen.value = true
  detailMessages.value = []
  const conversation = conversations.value.find((item) => item.id === conversationId)
  activeFlyerThreadId.value = !isUserChatMode.value && Array.isArray(conversation?.flyerThreads) ? conversation.flyerThreads[0]?.id || '' : ''
  loadConversationDetail(conversationId)
}

const closeCompactChat = () => {
  stopTyping()
  compactChatOpen.value = false
}

const ensureMessageThread = () => {
  if (!Array.isArray(activeConversation.value.flyerThreads)) {
    activeConversation.value.flyerThreads = []
  }

  const targetFlyerId = activeChatTarget.value.flyerId
  const existingThread = isFlyerThreadMode.value && targetFlyerId
    ? activeConversation.value.flyerThreads.find((thread) => String(getThreadFlyer(thread).id || thread.id) === String(targetFlyerId))
    : activeFlyerThread.value.id
      ? activeFlyerThread.value
      : activeConversation.value.flyerThreads[0]

  if (existingThread?.id) {
    activeFlyerThreadId.value = existingThread.id
    return existingThread
  }

  const newThread = {
    ...emptyThread,
    id: isFlyerThreadMode.value && targetFlyerId ? String(targetFlyerId) : `normal-${activeConversation.value.id || Date.now()}`,
    flyer: isFlyerThreadMode.value && targetFlyerId ? { ...emptyFlyer, id: targetFlyerId } : { ...emptyFlyer },
    messages: []
  }
  activeConversation.value.flyerThreads.push(newThread)
  activeFlyerThreadId.value = newThread.id
  return newThread
}

const getTypingPayload = () => {
  const userId = String(currentUserId() || '').trim()
  const toId = String(
    activeConversation.value?.contact?.id ||
    activeConversationId.value ||
    activeChatTarget.value.toId ||
    ''
  ).trim()
  const flyerId = isFlyerThreadMode.value
    ? String(activeChatTarget.value.flyerId || activeFlyerThread.value?.flyer?.id || '').trim()
    : ''
  if (!userId || !toId) return null

  return {
    user_id: userId,
    userid: userId,
    from_id: userId,
    to_id: toId,
    flyer_id: flyerId
  }
}

const stopTyping = () => {
  if (typingStopTimer) {
    clearTimeout(typingStopTimer)
    typingStopTimer = null
  }
  if (!localTypingActive) return

  const payload = getTypingPayload()
  if (payload) {
    emitChatRealtime('typing_stop', {
      ...payload,
      typing: false,
      is_typing: false
    })
  }
  localTypingActive = false
  lastTypingEmitAt = 0
}

const handleTypingInput = () => {
  const payload = getTypingPayload()
  if (!draft.value.trim() || !payload) {
    stopTyping()
    return
  }

  const now = Date.now()
  if (!localTypingActive || now - lastTypingEmitAt >= 800) {
    emitChatRealtime('typing_start', {
      ...payload,
      typing: true,
      is_typing: true
    })
    localTypingActive = true
    lastTypingEmitAt = now
  }

  if (typingStopTimer) clearTimeout(typingStopTimer)
  typingStopTimer = setTimeout(stopTyping, 1500)
}

const sendMessage = async () => {
  const body = draft.value.trim()
  if (!body) return
  const userid = currentUserId()
  const target = activeChatTarget.value
  const toId = String(activeConversation.value?.contact?.id || target.toId || '').trim()
  const shouldSendFlyerId = isFlyerThreadMode.value
  const flyerId = shouldSendFlyerId ? String(target.flyerId || activeFlyerThread.value?.flyer?.id || '').trim() : ''
  if (!userid || !toId) {
    detailError.value = 'Chat user is missing.'
    return
  }
  stopTyping()

  const sentAt = new Date().toISOString()
  const sentTime = formatChatTime(sentAt)
  const optimisticMessage = {
    id: Date.now(),
    mine: true,
    body,
    time: sentTime,
    dateValue: sentAt,
    pending: true
  }

  const messageThread = isFlyerThreadMode.value ? ensureMessageThread() : null
  if (messageThread) {
    if (!Array.isArray(messageThread.messages)) messageThread.messages = []
    messageThread.messages.push(optimisticMessage)
    messageThread.lastMessage = body
    messageThread.lastTime = sentTime
    messageThread.lastDateValue = sentAt
    messageThread.unread = 0
  } else {
    detailMessages.value.push(optimisticMessage)
  }
  activeConversation.value.lastMessage = body
  activeConversation.value.lastTime = sentTime
  activeConversation.value.lastDateValue = sentAt
  activeConversation.value.sortDateValue = sentAt
  activeConversation.value.unread = 0
  draft.value = ''
  emojiPickerOpen.value = false
  scrollMessagesToBottom()

  try {
    const payload = {
      userid,
      flyer_id: flyerId,
      to_id: toId,
      message: body
    }
    const { data } = await api.sendChatFlyer(payload)
    if (data?.socket_response) {
      console.log(`[Chats send_chat_flyer socket_response][selected user:${toId}]`, data.socket_response)
    }
    const socketResponseRow = extractRows(data?.socket_response)[0]
    if (socketResponseRow) {
      const confirmedMessage = normalizeMessage(socketResponseRow, `sent-${optimisticMessage.id}`)
      if (messageThread) {
        messageThread.messages = replaceOptimisticMessage(messageThread.messages || [], optimisticMessage.id, confirmedMessage)
        messageThread.lastMessage = confirmedMessage.body
        messageThread.lastTime = confirmedMessage.time
        messageThread.lastDateValue = confirmedMessage.dateValue
      } else {
        detailMessages.value = replaceOptimisticMessage(detailMessages.value, optimisticMessage.id, confirmedMessage)
      }
      activeConversation.value.lastMessage = confirmedMessage.body
      activeConversation.value.lastTime = confirmedMessage.time
      activeConversation.value.lastDateValue = confirmedMessage.dateValue
      activeConversation.value.sortDateValue = confirmedMessage.dateValue
      scrollMessagesToBottom()
    }
    if (shouldSendFlyerId) {
      clearFlyerChatTargetAfterFirstSend()
    }
  } catch (error) {
    if (messageThread) {
      messageThread.messages = messageThread.messages.filter((message) => message.id !== optimisticMessage.id)
    }
    detailMessages.value = detailMessages.value.filter((message) => message.id !== optimisticMessage.id)
    detailError.value = error?.response?.data?.message || error?.message || 'Message could not be sent.'
    draft.value = body
    handleTypingInput()
  }
}

const isDesktopComposerShortcut = () =>
  typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches

const handleComposerEnter = (event) => {
  if (event.shiftKey || !isDesktopComposerShortcut()) return
  event.preventDefault()
  sendMessage()
}

const insertEmoji = (event) => {
  const emoji = event.detail?.unicode || event.detail?.emoji?.unicode || ''
  if (!emoji) return
  draft.value += emoji
  emojiPickerOpen.value = false
  handleTypingInput()
}

const openShareFlyer = (flyer) => {
  if (!flyer) return
  shareFlyer.value = flyer
}

const loadConversations = async ({ preserveActive = false, silent = false } = {}) => {
  const userid = currentUserId()
  if (!userid) {
    chatsError.value = 'Please login to view chats.'
    conversations.value = []
    return
  }

  if (!silent) {
    chatsLoading.value = true
  }
  if (!preserveActive) {
    chatsError.value = ''
  }

  try {
    const { data } = await api.getChatsFlyerUser({ userid })
    const rows = extractRows(data)
    conversations.value = uniqueConversations(rows.map(normalizeConversation).filter(Boolean).filter(hasRealConversationContact))
    if (preserveActive) return
    const temporaryConversationId = ensureTemporaryTargetConversation(activeChatTarget.value)
    activeConversationId.value = temporaryConversationId || ''
    const temporaryConversation = conversations.value.find((conversation) => conversation.id === temporaryConversationId)
    activeFlyerThreadId.value = temporaryConversationId && !isUserChatMode.value && Array.isArray(temporaryConversation?.flyerThreads)
      ? temporaryConversation.flyerThreads[0]?.id || ''
      : ''
    compactChatOpen.value = Boolean(temporaryConversationId)
    detailMessages.value = []
    if (temporaryConversationId) loadConversationDetail(temporaryConversationId)
  } catch (error) {
    if (preserveActive) return
    conversations.value = []
    const temporaryConversationId = ensureTemporaryTargetConversation(activeChatTarget.value)
    activeConversationId.value = temporaryConversationId || ''
    const temporaryConversation = conversations.value.find((conversation) => conversation.id === temporaryConversationId)
    activeFlyerThreadId.value = temporaryConversationId && !isUserChatMode.value && Array.isArray(temporaryConversation?.flyerThreads)
      ? temporaryConversation.flyerThreads[0]?.id || ''
      : ''
    compactChatOpen.value = Boolean(temporaryConversationId)
    detailMessages.value = []
    if (temporaryConversationId) loadConversationDetail(temporaryConversationId)
    chatsError.value = temporaryConversationId
      ? ''
      : error?.response?.data?.message || error?.message || 'Conversations could not be loaded.'
  } finally {
    if (!silent) {
      chatsLoading.value = false
    }
  }
}

watch([activeConversationId, activeFlyerThreadId], () => {
  connectChatSocket()
  if (!activeConversation.value.id) return
  activeConversation.value.unread = 0
  if (activeFlyerThread.value.id) {
    activeFlyerThread.value.unread = 0
  }
  emojiPickerOpen.value = false
  scrollMessagesToBottom()
}, { immediate: true })

onMounted(() => {
  window.addEventListener(SESSION_LOGOUT_EVENT, handleSessionLogout)
  loadConversations()
})
onBeforeUnmount(() => {
  window.removeEventListener(SESSION_LOGOUT_EVENT, handleSessionLogout)
  stopTyping()
  cleanupChatRealtime()
})
</script>

<style scoped>
@media (max-width: 1023px) {
  .chat-shell {
    height: calc(100dvh - 5.75rem);
  }

  .chat-panel {
    height: 100%;
    max-height: 100%;
  }
}

.chat-pane {
  background-color: #eff6ff;
  background-image:
    radial-gradient(circle at 1px 1px, rgba(37, 99, 235, 0.16) 1px, transparent 0),
    radial-gradient(circle at 22px 22px, rgba(14, 165, 233, 0.1) 1.5px, transparent 0),
    linear-gradient(45deg, transparent 47%, rgba(37, 99, 235, 0.08) 48%, rgba(37, 99, 235, 0.08) 52%, transparent 53%),
    linear-gradient(-45deg, transparent 47%, rgba(14, 165, 233, 0.06) 48%, rgba(14, 165, 233, 0.06) 52%, transparent 53%),
    linear-gradient(135deg, rgba(37, 99, 235, 0.08), rgba(255, 255, 255, 0.68));
  background-size: 24px 24px, 48px 48px, 56px 56px, 56px 56px, 100% 100%;
}

.chat-bubble {
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.chat-bubble-out {
  background: linear-gradient(135deg, #60a5fa, #3b82f6);
  box-shadow: 0 8px 18px rgba(37, 99, 235, 0.12);
}

.chat-bubble-in {
  background: rgba(248, 251, 255, 0.96);
  border: 1px solid rgba(191, 219, 254, 0.62);
  box-shadow: 0 6px 14px rgba(15, 23, 42, 0.06);
}

.chat-link-preview {
  max-width: min(22rem, 100%);
  text-decoration: none;
}

.chat-link-preview-out {
  background: rgba(255, 255, 255, 0.16);
  color: #ffffff;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.18);
}

.chat-link-preview-in {
  background: #ffffff;
  color: rgb(15 23 42);
  box-shadow: inset 0 0 0 1px rgba(191, 219, 254, 0.72);
}

.flyer-image-placeholder {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.14), rgba(14, 165, 233, 0.24));
  color: rgb(29, 78, 216);
  font-size: 1rem;
  font-weight: 800;
  text-transform: uppercase;
}

.flyer-thread-description {
  overflow-wrap: anywhere;
}

.flyer-thread-description :deep(p),
.flyer-thread-description :deep(ul),
.flyer-thread-description :deep(ol) {
  margin: 0;
}

.flyer-thread-description :deep(ul),
.flyer-thread-description :deep(ol) {
  padding-left: 1rem;
}

.flyer-thread-description :deep(a) {
  color: rgb(37 99 235);
  font-weight: 500;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.flyer-thread-description :deep(strong),
.flyer-thread-description :deep(b) {
  font-weight: 600;
  color: rgb(51 65 85);
}

:deep(.chat-emoji-picker) {
  width: min(21rem, calc(100vw - 2rem));
  height: 24rem;
  --border-radius: 1rem;
  --outline-color: rgb(37 99 235);
}
</style>
